import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import XGSIcon from "../icon/xgsIcon";
import XGSIcons from "../icon/xgsIcons";
import { cn } from "../../services/common/className";
import type { DataTableColumnOption } from "./dataTableTypes";

import "./dataTableToolbar.scss";

export interface DataTableToolbarProps {
  columnOptions: DataTableColumnOption[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (
    updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => void;
  /** Optional label for the column selector button */
  columnSelectorLabel?: string;
}

const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  columnOptions,
  columnVisibility,
  onColumnVisibilityChange,
  columnSelectorLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; right: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setDropdownRect(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Close on scroll/resize so the portaled menu doesn't float away from the gear
  // (nested trip tables live inside scroll containers).
  useEffect(() => {
    if (!open) return;

    const handleClose = (e?: Event) => {
      // Allow scrolling inside the dropdown's own list
      if (
        e?.type === "scroll" &&
        e.target instanceof Node &&
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener("resize", handleClose);
    // capture: true so we catch scroll on nested overflow containers, not just window
    window.addEventListener("scroll", handleClose, true);
    return () => {
      window.removeEventListener("resize", handleClose);
      window.removeEventListener("scroll", handleClose, true);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const dropdownContent = open && dropdownRect && (
    <div
      ref={dropdownRef}
      className="xgs-data-table-toolbar__dropdown xgs-data-table-toolbar__dropdown--portal"
      role="menu"
      aria-label="Column visibility"
      style={{
        position: "fixed",
        top: dropdownRect.top,
        right: dropdownRect.right,
        left: "auto",
      }}
    >
      <div className="xgs-data-table-toolbar__dropdown-title">Show columns</div>
      {columnOptions.map(({ id, label }) => (
        <label key={id} className="xgs-data-table-toolbar__dropdown-item">
          <input
            type="checkbox"
            checked={columnVisibility[id] !== false}
            onChange={() => {
              onColumnVisibilityChange((prev) => ({
                ...prev,
                [id]: !prev[id],
              }));
            }}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="xgs-data-table-toolbar" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        className={cn("xgs-data-table-toolbar__column-toggle")({ active: open })}
        onClick={() => setOpen((o) => !o)}
        title="Choose columns"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <XGSIcon icon={XGSIcons.faCog} />
        {columnSelectorLabel ? (
          <span className="xgs-data-table-toolbar__btn-label">{columnSelectorLabel}</span>
        ) : null}
      </button>
      {typeof document !== "undefined" && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DataTableToolbar;
